import React from "react";

interface SummaryChip {
  title: string;
  meta: string;
  main: string;
  footer: string;
  badge: {
    text: string;
    color: "blue" | "amber" | "red" | "green";
  };
}

const summaryData: SummaryChip[] = [
  {
    title: "Portfolio by risk band",
    meta: "Share of outstanding balance",
    main: "68% low · 22% mid · 10% high",
    footer:
      "High-risk band remains below the 15% internal threshold – mainly concentrated in smaller metros.",
    badge: { text: "Balanced", color: "blue" },
  },
  {
    title: "Top province exposure",
    meta: "Balance distribution",
    main: "ON 44% · BC 27% · QC 15%",
    footer:
      "Exposure remains skewed to Ontario, with BC and Quebec as secondary anchors – all within current policy bands.",
    badge: { text: "ON-led", color: "amber" },
  },
  {
    title: "Risk-adjusted yield",
    meta: "After regional loss curves",
    main: "+3.1%",
    footer:
      "After accounting for regional loss expectations, the book still delivers ~310 bps over 5-yr GoC benchmark.",
    badge: { text: "Attractive", color: "green" },
  },
];

export const ConcentrationRiskSummary: React.FC = () => {
  const getBadgeClasses = (color: string) => {
    switch (color) {
      case "blue":
        return "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]";
      case "amber":
        return "border-[rgba(255,184,0,0.8)] text-[#ffb800] bg-[rgba(255,184,0,0.1)]";
      case "red":
        return "border-[rgba(255,51,102,0.8)] text-[#ff3366] bg-[rgba(255,51,102,0.1)]";
      case "green":
        return "border-[rgba(0,255,136,0.8)] text-neon-green bg-[rgba(0,255,136,0.12)]";
      default:
        return "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]";
    }
  };

  return (
    <div className="relative p-4 pb-4.5 rounded-[18px] bg-gradient-to-br from-[#22284d] to-[#10142f] border border-[rgba(139,146,176,0.2)] shadow-[0_16px_40px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex flex-col gap-0.5">
          <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
            Concentration & Risk summary
          </div>
          <div className="text-[11px] text-[#8b92b0]">
            Quick snapshot of exposure by risk band and geography.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2.5">
        {summaryData.map((summary, idx) => (
          <div
            key={idx}
            className="p-3.5 rounded-[12px] bg-[rgba(5,7,22,0.95)] border border-[rgba(139,146,176,0.5)] text-xs flex flex-col gap-1.5"
          >
            <div className="flex items-center justify-between gap-1.5">
              <div>
                <div className="text-xs font-medium">{summary.title}</div>
                <div className="text-[11px] text-[#8b92b0]">{summary.meta}</div>
              </div>
              <span
                className={`px-2.5 py-1 rounded-full text-[10px] border ${getBadgeClasses(
                  summary.badge.color
                )}`}
              >
                {summary.badge.text}
              </span>
            </div>
            <div className="text-[17px] font-jetbrains-mono font-semibold">
              {summary.main}
            </div>
            <div className="text-[11px] text-[#8b92b0]">{summary.footer}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
